import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import type { Navigation, NavigationPageItem } from '../types/navigation';
import { useModules } from '../api/endpoints/useModules';
import type { ModuleItem } from '../api/endpoints/useModules';
import { useNavBadgeCounts } from '../api/endpoints/useNavBadgeCounts';
import {
  buildModulesNavigation,
  findFirstLeafPath,
  type ModuleLeafRoute,
} from '../utils/modulesNavigation';
import { applyNavBadges } from '../utils/navBadge';
import { useNavigationTooltips } from './useNavigationTooltips';
import { useTitleHeaders } from './useTitleHeaders';
import { useActiveNavGroups } from './useActiveNavGroups';

const EMPTY_NAVIGATION_SIGNATURE = '[]';

function buildNavigationSignature(items: Navigation): string {
  const serialize = (entries: Navigation): Array<Record<string, unknown>> =>
    entries.map(entry => {
      if (entry.kind === 'header') {
        return { kind: 'header', title: entry.title };
      }
      if (entry.kind === 'divider') {
        return { kind: 'divider' };
      }

      const pageEntry = entry as NavigationPageItem;
      return {
        segment: pageEntry.segment,
        title: pageEntry.title,
        pattern: pageEntry.pattern,
        children: serialize(pageEntry.children ?? []),
      };
    });

  try {
    return JSON.stringify(serialize(items));
  } catch {
    return EMPTY_NAVIGATION_SIGNATURE;
  }
}

interface UseAppNavigationResult {
  navigation: Navigation;
  bottomNavigation: Navigation;
  moduleLeaves: ModuleLeafRoute[];
  navigationSignature: string;
  firstLeafPath: string | null;
  isModulesLoading: boolean;
}

export function useAppNavigation(clientId: string): UseAppNavigationResult {
  const location = useLocation();
  const { t } = useTranslation();
  const { data: modulesResponse, isLoading: isModulesLoading } =
    useModules(clientId);
  const frontendRouteFallbackTitle = t(
    'dashboard.navigation.frontendRouteTitle',
    'Frontend Route'
  );

  const {
    navigation: builtNavigation,
    bottomNavigation: builtBottomNavigation,
    leaves: moduleLeaves,
  } = useMemo(() => {
    if (!clientId) {
      return {
        navigation: [] as Navigation,
        bottomNavigation: [] as Navigation,
        leaves: [] as ModuleLeafRoute[],
      };
    }

    const data = modulesResponse?.data;
    const modulesRoot =
      data && 'modules' in data
        ? (data as { modules?: ModuleItem[] }).modules
        : [];
    const bottomRoot =
      data && 'bottomModules' in data
        ? (data as { bottomModules?: ModuleItem[] }).bottomModules
        : [];

    const main = buildModulesNavigation(
      modulesRoot,
      clientId,
      frontendRouteFallbackTitle
    );
    const bottom = buildModulesNavigation(
      bottomRoot,
      clientId,
      frontendRouteFallbackTitle
    );

    return {
      navigation: main.navigation,
      bottomNavigation: bottom.navigation,
      // Leaves are placement-agnostic — routing, tooltips, active-group
      // highlighting and breadcrumbs must resolve against both roots.
      leaves: [...main.leaves, ...bottom.leaves],
    };
  }, [modulesResponse?.data, clientId, frontendRouteFallbackTitle]);

  // Counts are fetched off the built leaves and applied back onto the tree,
  // rather than being an input to the build: the build stays a pure function of
  // the modules config, so an arriving count cannot feed back into the memo
  // that produced the leaves it was fetched for.
  const badgeCounts = useNavBadgeCounts(clientId, moduleLeaves);

  const navigation = useMemo(
    () => applyNavBadges(builtNavigation, badgeCounts),
    [builtNavigation, badgeCounts]
  );
  const bottomNavigation = useMemo(
    () => applyNavBadges(builtBottomNavigation, badgeCounts),
    [builtBottomNavigation, badgeCounts]
  );

  const navigationSignature = useMemo(
    // Keep the two roots' signatures distinct so moving an entry between the
    // main and bottom nav always changes the signature — a flattened
    // concatenation could stay identical across such a move and skip the
    // tooltip/title-header effects that key off it.
    () =>
      JSON.stringify([
        buildNavigationSignature(navigation),
        buildNavigationSignature(bottomNavigation),
      ]),
    [navigation, bottomNavigation]
  );

  const firstLeafPath = useMemo(
    () => findFirstLeafPath(moduleLeaves),
    [moduleLeaves]
  );

  useNavigationTooltips(navigationSignature);
  useTitleHeaders(navigationSignature);
  useActiveNavGroups({
    pathname: location.pathname,
    leaves: moduleLeaves,
    navigationSignature,
  });

  return {
    navigation,
    bottomNavigation,
    moduleLeaves,
    navigationSignature,
    firstLeafPath,
    isModulesLoading,
  };
}
